import { useState, useEffect, useRef, useCallback } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  TextField,
  CircularProgress,
  Divider,
  Input,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Add, Remove } from '@mui/icons-material';
import ModalRegisterClient from '../../scenes/createUser/ModalRegisterClient';
import DocumentUploadSection from '../DocumentUploadSection';
import { DOCUMENT_TYPES } from '../../scenes/customers/form/RegisterTab';
import CustomDateInstallments from './CustomDateInstallments';
import { useTranslation } from '../../hooks/useTranslation';
import { tokens } from '../../theme';
import api from '../../services/api';
import { HOST_API } from '../../config/hosts';
import { toast } from 'react-toastify';
import {
  formatCurrency,
  formatPercent,
  parseMonetaryValue,
  parsePercentValue,
  parseISOToDate,
} from '../../utils';
import { FIXED_INSTALLMENT_OPTIONS } from '../../constants';

const PAGE_SIZE = 20;
const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const fieldError = (touched, errors, name) => (touched[name] && errors[name] ? errors[name] : '');

const buildSchema = (t) =>
  yup.object({
    client: yup
      .mixed()
      .nullable()
      .required(t('validation.clientRequired'))
      .test(
        'is-object',
        t('validation.clientRequired'),
        (v) => v !== null && typeof v === 'object'
      ),
    total: yup
      .string()
      .required(t('validation.totalRequired'))
      .test('gt-zero', t('validation.totalGtZero'), (v) => parseMonetaryValue(v) > 0),
    firstPayment: yup
      .string()
      .required(t('validation.entryRequired'))
      .test('lt-total', t('validation.entryLtTotal'), function (v) {
        return parseMonetaryValue(v) < parseMonetaryValue(this.parent.total);
      }),
    iof: yup.string().default(''),
    tac: yup.string().default(''),
    interest: yup.string().default(''),
    operational: yup.string().default(''),
    paymentMethod: yup.string().oneOf(['fixed', 'customized']).required(),
    parcelas: yup.number().integer().required(),
    paymentDate: yup.string().optional(),
  });

export const parsePaymentType = (value) => {
  if (value === 'parcelamento_fixo') return 'fixed';
  if (value === 'data_personalizada') return 'customized';
  return 'fixed';
};

const SimulationForm = ({
  simulation,
  customerId,
  simulatorConfig,
  onSimulate,
  loadingSimulate,
  onDataChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  // --- Clientes paginados ---------------------------------------------------
  const [clients, setClients] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectOpen, setSelectOpen] = useState(false);
  const [preSelectedClient, setPreSelectedClient] = useState(null);
  const listEndRef = useRef(null);
  const clientSectionRef = useRef(null);

  const fetchClients = useCallback(
    async (page = 1, append = false) => {
      if (loadingList) return;
      if (append && page > totalPages) return;
      try {
        setLoadingList(true);
        const res = await api.get(`/v1/customer/list?page=${page}&limit=${PAGE_SIZE}`);
        const newItems = res.data?.list ?? [];
        const newTotal = res.data?.totalPages ?? 1;
        setTotalPages(newTotal);
        setCurrentPage(page);
        setClients((prev) => (append ? [...prev, ...newItems] : newItems));
      } catch (err) {
        console.error(t('messages.errorFetchingData'), err);
      } finally {
        setLoadingList(false);
      }
    },
    [loadingList, totalPages, t]
  );

  useEffect(() => {
    if (!selectOpen) return;
    setClients([]);
    setCurrentPage(1);
    setTotalPages(1);
    fetchClients(1, false);
  }, [selectOpen]);

  useEffect(() => {
    const el = listEndRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingList && currentPage < totalPages)
          fetchClients(currentPage + 1, true);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listEndRef.current, loadingList, currentPage, totalPages]);

  useEffect(() => {
    if (!customerId || simulation) return;
    api
      .get(`${HOST_API}/v1/customer/${customerId}`)
      .then(({ data }) => setPreSelectedClient(data))
      .catch(() => toast.error('Erro ao buscar cliente'));
  }, [customerId]);

  // --- Documentos -----------------------------------------------------------
  const [documentsByType, setDocumentsByType] = useState(
    Object.fromEntries(DOCUMENT_TYPES.map(({ key }) => [key, null]))
  );
  const [loadingSaveDocuments, setLoadingSaveDocuments] = useState(false);

  const loadDocumentsByType = (savedDocuments) => {
    const byFieldName = Object.fromEntries(
      (savedDocuments || []).filter((d) => d.type).map((d) => [d.type, d])
    );
    setDocumentsByType(
      Object.fromEntries(
        DOCUMENT_TYPES.map(({ key, fieldName }) => [key, byFieldName[fieldName] || null])
      )
    );
  };

  const handleSetDocument = (typeKey, newFiles) => {
    setDocumentsByType((prev) => ({ ...prev, [typeKey]: newFiles[0] || null }));
  };

  const handleClearDocument = (typeKey) => {
    setDocumentsByType((prev) => ({ ...prev, [typeKey]: null }));
  };

  useEffect(() => {
    if (!simulation) return;
    loadDocumentsByType(simulation.custom_fields?.documents);
  }, [simulation?.id]);

  // --- Parcelas personalizadas - states ------------------------------------
  const [isCustomInstallments, setIsCustomInstallments] = useState(
    simulation?.has_custom_installment ?? false
  );
  const [inputInstallment, setInputInstallment] = useState('1');
  const customInstallmentsLockedRef = useRef(false);

  const [customDateInstallments, setCustomDateInstallments] = useState([
    { id: 'init-0', date: '', value: '0,00' },
  ]);

  useEffect(() => {
    if (!simulation) return;
    if (simulation.custom_dates?.length) {
      customInstallmentsLockedRef.current = true;
      setCustomDateInstallments(
        simulation.custom_dates.map((d) => ({
          id: generateId(),
          date: typeof d.date === 'string' ? d.date.split('T')[0] : '',
          value: formatCurrency(parseFloat(d.value) || 0),
        }))
      );
    } else if (simulation.first_due_date && simulation.payment_type === 'data_personalizada') {
      setCustomDateInstallments([
        {
          id: generateId(),
          date: parseISOToDate(simulation.first_due_date),
          value: formatCurrency(parseMonetaryValue(String(simulation.financed_value || 0))),
        },
      ]);
    }
  }, [simulation?.id]);

  // --- Modal novo cliente --------------------------------------------------
  const [createNewClient, setCreateNewClient] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);

  const handleCreateClient = async (payload) => {
    try {
      setLoadingClient(true);
      const { data: created } = await api.post(`${HOST_API}/v1/customer`, payload);
      setCreateNewClient(false);
      toast.success(t('messages.clientRegisteredSuccess'));
      if (created?.id) {
        try {
          const { data: fullCustomer } = await api.get(`${HOST_API}/v1/customer/${created.id}`);
          setFieldValue('client', fullCustomer);
        } catch {
          console.warn('Falha ao buscar dados completos do cliente criado:', created.id);
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.errorRegisterClient'));
    } finally {
      setLoadingClient(false);
    }
  };

  // --- Formik --------------------------------------------------------------
  const initialValues = {
    client: simulation ?? preSelectedClient ?? null,
    total: simulation ? formatCurrency(simulation.total_value) : '00,00',
    firstPayment: simulation ? formatCurrency(simulation.entry_value) : '00,00',
    iof: simulation?.iof_rate != null ? formatPercent(simulation.iof_rate) : '',
    tac: simulation?.tac_rate != null ? formatPercent(simulation.tac_rate) : '',
    interest: simulation?.interest_rate != null ? formatPercent(simulation.interest_rate) : '',
    operational: simulation?.operational_fee != null ? formatCurrency(simulation.operational_fee) : '',
    paymentMethod: parsePaymentType(simulation?.payment_type),
    parcelas: simulation ? simulation.installments : 6,
    paymentDate: simulation ? parseISOToDate(simulation?.first_due_date) : '',
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: buildSchema(t),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const isFixed = values.paymentMethod === 'fixed';

      if (!isFixed) {
        const missingDate = customDateInstallments.find((i) => !i.date);
        if (missingDate) {
          toast.error(t('validation.paymentDateRequired'));
          return;
        }
      }

      const clientId = values.client?.id ?? values.client?.customer_id;
      const clientName = values.client?.full_name ?? values.client?.client_name;
      const clientDocument = values.client?.taxvar ?? values.client?.client_document;

      const payload = {
        customer_id: clientId,
        client_name: clientName,
        client_document: clientDocument,
        release_date: new Date(),
        total_value: parseMonetaryValue(values.total),
        entry_value: parseMonetaryValue(values.firstPayment),
        financed_value: parseMonetaryValue(valueLoan),
        interest_rate: parsePercentValue(values.interest),
        iof_rate: parsePercentValue(values.iof),
        tac_rate: parsePercentValue(values.tac),
        operational_fee: parseMonetaryValue(values.operational),
        payment_type: isFixed ? 'parcelamento_fixo' : 'data_personalizada',
        installments: isFixed ? parseInt(values.parcelas) : customDateInstallments.length,
        first_due_date: isFixed ? null : customDateInstallments[0]?.date || null,
        custom_dates: isFixed
          ? null
          : customDateInstallments.map((d) => ({
              date: d.date,
              value: parseMonetaryValue(d.value),
            })),
        has_custom_installment: isCustomInstallments,
        documentsByType,
      };

      onSimulate(payload);
    },
  });

  const { values, errors, touched, setFieldValue, handleBlur } = formik;

  // Carrega documentos do cliente ao selecioná-lo (nova simulação)
  useEffect(() => {
    if (simulation) return;
    loadDocumentsByType(values.client?.custom_fields?.documents);
  }, [values.client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveClientDocuments = async () => {
    if (!values.client) return;
    try {
      setLoadingSaveDocuments(true);
      const clientId = values.client?.id ?? values.client?.customer_id;
      const { data: customer } = await api.get(`${HOST_API}/v1/customer/${clientId}`);

      const formData = new FormData();
      formData.append('fullName', customer.full_name || '');
      formData.append('email', customer.email || '');
      formData.append('phone', customer.cellphone || '');
      formData.append('taxvar', customer.taxvar || '');

      const existingPartners = (customer.relationships || []).map((rel) => ({
        id: rel.people?.id || '',
        fullName: rel.people?.full_name || '',
        email: rel.people?.email || '',
        phone: rel.people?.phone || '',
        taxvar: rel.people?.taxvar || '',
        document: rel.people?.document || '',
      }));
      formData.append('partners', JSON.stringify(existingPartners));

      const existingDocs = [];
      DOCUMENT_TYPES.forEach(({ key, fieldName }) => {
        const doc = documentsByType[key];
        if (!doc) return;
        if (doc instanceof File) {
          formData.append(`documents[${fieldName}]`, doc);
        } else {
          existingDocs.push({ ...doc, type: fieldName });
        }
      });
      if (existingDocs.length) {
        formData.append('existingDocuments', JSON.stringify(existingDocs));
      }

      const { data: updated } = await api.put(`${HOST_API}/v1/customer/${clientId}`, formData);
      loadDocumentsByType(updated.custom_fields?.documents);
      toast.success(t('messages.clientUpdatedSuccess'));
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.errorRegisterClient'));
    } finally {
      setLoadingSaveDocuments(false);
    }
  };

  const isPaymentMethodFixed = values.paymentMethod === 'fixed';

  const valueLoan = (() => {
    const v = parseMonetaryValue(values.total) - parseMonetaryValue(values.firstPayment);
    return v > 0 ? formatCurrency(v) : '00,00';
  })();

  const isFirstRender = useRef(true);

  const notifyChange = useCallback(() => {
    if (isFirstRender.current) return;
    onDataChange?.();
  }, [onDataChange]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
  }, []);

  const prevValuesRef = useRef(null);

  useEffect(() => {
    if (isFirstRender.current) return;

    const snapshot = {
      total: values.total,
      firstPayment: values.firstPayment,
      iof: values.iof,
      tac: values.tac,
      interest: values.interest,
      operational: values.operational,
      paymentMethod: values.paymentMethod,
      parcelas: values.parcelas,
    };

    if (prevValuesRef.current === null) {
      prevValuesRef.current = snapshot;
      return;
    }

    const changed = Object.keys(snapshot).some((k) => snapshot[k] !== prevValuesRef.current[k]);

    if (changed) {
      prevValuesRef.current = snapshot;
      notifyChange();
    }
  }, [
    values.total,
    values.firstPayment,
    values.iof,
    values.tac,
    values.interest,
    values.operational,
    values.paymentMethod,
    values.parcelas,
    notifyChange,
  ]);

  // --- Parcelas personalizadas - logica de distribuicao (depende de valueLoan)

  const distributeEvenly = useCallback((totalLoan, items) => {
    const count = items.length;
    if (count === 0 || totalLoan <= 0) return items;
    const baseVal = Math.floor((totalLoan * 100) / count) / 100;
    const lastVal = parseFloat((totalLoan - baseVal * (count - 1)).toFixed(2));
    return items.map((item, idx) => ({
      ...item,
      value: formatCurrency(idx === count - 1 ? Math.max(0, lastVal) : baseVal),
    }));
  }, []);

  // Redistribui ao mudar total/entrada (apenas quando nao editado manualmente)
  useEffect(() => {
    if (values.paymentMethod !== 'customized') return;
    if (customInstallmentsLockedRef.current) return;
    const vLoan = parseMonetaryValue(valueLoan);
    if (vLoan <= 0) return;
    setCustomDateInstallments((prev) => distributeEvenly(vLoan, prev));
  }, [valueLoan, values.paymentMethod, distributeEvenly]);

  const handleAddCustomDate = useCallback(() => {
    customInstallmentsLockedRef.current = false;
    setCustomDateInstallments((prev) => {
      const vLoan = parseMonetaryValue(valueLoan);
      const newItems = [...prev, { id: generateId(), date: '', value: '0,00' }];
      return distributeEvenly(vLoan > 0 ? vLoan : 0, newItems);
    });
    notifyChange();
  }, [valueLoan, distributeEvenly, notifyChange]);

  const handleRemoveCustomDate = useCallback(
    (id) => {
      customInstallmentsLockedRef.current = false;
      setCustomDateInstallments((prev) => {
        if (prev.length <= 1) return prev;
        const remaining = prev.filter((i) => i.id !== id);
        const vLoan = parseMonetaryValue(valueLoan);
        return distributeEvenly(vLoan > 0 ? vLoan : 0, remaining);
      });
      notifyChange();
    },
    [valueLoan, distributeEvenly, notifyChange]
  );

  const handleCustomDateChange = useCallback(
    (id) => (e) => {
      const date = e.target.value;
      setCustomDateInstallments((prev) => prev.map((i) => (i.id === id ? { ...i, date } : i)));
      notifyChange();
    },
    [notifyChange]
  );

  // Ao editar manualmente uma parcela, recalcula as demais mantendo o total
  const handleCustomValueChange = useCallback(
    (id) => (e) => {
      customInstallmentsLockedRef.current = true;

      const raw = e.target.value.replace(/[^\d.,]/g, '');
      const onlyDigits = raw.replace(/[.,]/g, '');
      let formatted;
      if (!onlyDigits) {
        formatted = '0,00';
      } else {
        const padded = onlyDigits.padStart(3, '0');
        const cents = padded.slice(-2);
        const intRaw = padded.slice(0, -2).replace(/^0+/, '') || '0';
        const intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        formatted = `${intFormatted},${cents}`;
      }

      const newVal = Math.max(parseMonetaryValue(formatted), 0);

      setCustomDateInstallments((prev) => {
        const vLoan = parseMonetaryValue(valueLoan);
        const totalCap = vLoan > 0 ? vLoan : 0;

        if (prev.length === 1) {
          return [
            {
              ...prev[0],
              value: formatCurrency(totalCap > 0 ? Math.min(newVal, totalCap) : newVal),
            },
          ];
        }

        const editedIdx = prev.findIndex((i) => i.id === id);
        if (editedIdx === -1) return prev;

        const sumBefore = prev
          .slice(0, editedIdx)
          .reduce((sum, i) => sum + parseMonetaryValue(i.value), 0);

        const afterCount = prev.length - editedIdx - 1;
        const minAfter = afterCount * 0.01;
        const maxEdited = parseFloat((totalCap - sumBefore - minAfter).toFixed(2));
        const safeVal = totalCap > 0 ? Math.min(newVal, Math.max(0, maxEdited)) : newVal;
        const safeFormatted = formatCurrency(safeVal);

        if (afterCount === 0) {
          return prev.map((item) => (item.id === id ? { ...item, value: safeFormatted } : item));
        }

        const remainder = parseFloat((totalCap - sumBefore - safeVal).toFixed(2));
        const baseOther = Math.floor((remainder * 100) / afterCount) / 100;
        const lastOther = parseFloat((remainder - baseOther * (afterCount - 1)).toFixed(2));

        let afterIdx = 0;
        return prev.map((item, idx) => {
          if (item.id === id) return { ...item, value: safeFormatted };
          if (idx > editedIdx) {
            const isLastAfter = afterIdx === afterCount - 1;
            const val = Math.max(0.01, isLastAfter ? lastOther : baseOther);
            afterIdx++;
            return { ...item, value: formatCurrency(val) };
          }
          return item;
        });
      });

      notifyChange();
    },
    [valueLoan, notifyChange]
  );

  // --- Handlers de formato -------------------------------------------------
  const handleMonetaryChange = (fieldName) => (e) => {
    customInstallmentsLockedRef.current = false;
    const raw = e.target.value.replace(/[^\d.,]/g, '');
    const onlyDigits = raw.replace(/[.,]/g, '');
    if (!onlyDigits) {
      setFieldValue(fieldName, '0,00', true);
      return;
    }
    const padded = onlyDigits.padStart(3, '0');
    const cents = padded.slice(-2);
    const intRaw = padded.slice(0, -2).replace(/^0+/, '') || '0';
    const intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    setFieldValue(fieldName, `${intFormatted},${cents}`, true);
  };

  const handlePercentChange = (fieldName) => (e) => {
    customInstallmentsLockedRef.current = false;
    const raw = e.target.value.replace(/[^\d,]/g, '');
    const onlyDigits = raw.replace(/,/g, '');
    if (!onlyDigits) {
      setFieldValue(fieldName, '0,00', true);
      return;
    }
    const padded = onlyDigits.padStart(6, '0');
    const cents = padded.slice(-4);
    const intRaw = padded.slice(0, -4).replace(/^0+/, '') || '0';
    if (intRaw.length > 2) return;
    setFieldValue(fieldName, `${intRaw},${cents}`, true);
  };

  const incrementValue = () => {
    const newValue = values.parcelas + 1;
    setFieldValue('parcelas', newValue);
    setInputInstallment(String(newValue));
  };

  const decreaseValue = () => {
    if (values.parcelas > 1) {
      const newValue = values.parcelas - 1;
      setFieldValue('parcelas', newValue);
      setInputInstallment(String(newValue));
    }
  };

  useEffect(() => {
    setInputInstallment(String(values.parcelas));
  }, [values.parcelas]);

  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isSubmitting && formik.errors.client) {
      clientSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formik.submitCount, formik.isSubmitting, formik.errors.client]);

  // --- Render --------------------------------------------------------------
  return (
    <>
      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Cliente */}
        <Paper
          ref={clientSectionRef}
          sx={{ p: 3, mb: 3, borderRadius: 2, border: 1, borderColor: '#E5E7EB' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonIcon sx={{ color: '#6B7280', mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#2C2C2C', fontWeight: 600 }}>
              {t('simulation.client')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            {t('simulation.selectClient')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={touched.client && !!errors.client}>
                <Select
                  name="client"
                  value={values.client || ''}
                  open={selectOpen}
                  onOpen={() => setSelectOpen(true)}
                  onClose={() => setSelectOpen(false)}
                  onChange={(e) => setFieldValue('client', e.target.value, true)}
                  onBlur={handleBlur}
                  displayEmpty
                  disabled={Boolean(simulation ?? customerId)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                  renderValue={(value) => {
                    if (!value)
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SearchIcon sx={{ color: '#6B7280' }} />
                          <Box>
                            <Typography variant="body2" sx={{ color: '#2C2C2C', fontWeight: 600 }}>
                              {t('simulation.getClient')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                              {t('simulation.getExistingCustomer')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    return (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C2C2C' }}>
                        {value.full_name || value.client_name}
                      </Typography>
                    );
                  }}
                  sx={{
                    maxHeight: 56,
                    bgcolor: '#F3F4F6',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: touched.client && errors.client ? 'error.main' : '#E5E7EB',
                    },
                  }}
                >
                  {!loadingList && clients.length === 0 && (
                    <MenuItem disabled>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        {t('messages.noClientsFound')}
                      </Typography>
                    </MenuItem>
                  )}
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {client.full_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                          CPF: {client.taxvar}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem
                    disabled
                    ref={listEndRef}
                    sx={{ justifyContent: 'center', py: 1, minHeight: 'auto' }}
                  >
                    {loadingList && (
                      <CircularProgress size={20} sx={{ color: colors.greenAccent[500] }} />
                    )}
                    {!loadingList && currentPage >= totalPages && clients.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        {t('simulation.allClientsLoaded')}
                      </Typography>
                    )}
                  </MenuItem>
                </Select>
                {touched.client && errors.client && (
                  <FormHelperText>{errors.client}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                onClick={() => setCreateNewClient(true)}
                fullWidth
                variant="outlined"
                startIcon={<PersonAddIcon />}
                sx={{
                  height: 56,
                  bgcolor: '#F3F4F6',
                  borderColor: '#E5E7EB',
                  color: '#2C2C2C',
                  textTransform: 'none',
                  justifyContent: 'center',
                  px: 2,
                  '&:hover': { borderColor: colors.greenAccent[500], bgcolor: '#F9FFF5' },
                }}
              >
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('simulation.newClient')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    {t('simulation.registerClient')}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>

          {values.client && (
            <Box mt={3}>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontSize: '16px', fontWeight: 700, mb: 2, color: '#374151' }}
              >
                Documentos do cliente
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {DOCUMENT_TYPES.map(({ key, label, hint }) => (
                  <DocumentUploadSection
                    key={key}
                    title={label}
                    files={documentsByType[key] ? [documentsByType[key]] : []}
                    onAddFiles={(files) => handleSetDocument(key, files)}
                    onRemoveFile={() => handleClearDocument(key)}
                    maxFiles={1}
                    hint={hint}
                  />
                ))}
              </Box>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  disabled={loadingSaveDocuments}
                  onClick={handleSaveClientDocuments}
                  sx={{
                    bgcolor: colors.greenAccent[500],
                    color: '#2C2C2C',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': { bgcolor: '#73C43D' },
                  }}
                >
                  {loadingSaveDocuments ? (
                    <CircularProgress size={20} sx={{ color: '#2C2C2C' }} />
                  ) : (
                    'Salvar'
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Dados da Simulacao */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: 1, borderColor: '#E5E7EB' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DescriptionIcon sx={{ color: '#6B7280', mr: 1 }} />
            <Typography variant="h6" sx={{ color: '#2C2C2C', fontWeight: 600 }}>
              {t('simulation.simulationDate')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
            {t('simulation.configureValueAndInterest')}
          </Typography>

          {/* Valores */}
          <Typography variant="subtitle2" sx={{ color: '#2C2C2C', fontWeight: 700, mb: 1 }}>
            {t('simulation.values')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
                {t('simulation.totalValue')}
              </Typography>
              <TextField
                fullWidth
                name="total"
                value={values.total}
                onChange={handleMonetaryChange('total')}
                onBlur={handleBlur}
                placeholder="R$ 10.000,00"
                error={touched.total && !!errors.total}
                helperText={fieldError(touched, errors, 'total')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F3F4F6' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
                {t('simulation.entry')}
              </Typography>
              <TextField
                fullWidth
                name="firstPayment"
                value={values.firstPayment}
                onChange={handleMonetaryChange('firstPayment')}
                onBlur={handleBlur}
                placeholder="R$ 1.000,00"
                error={touched.firstPayment && !!errors.firstPayment}
                helperText={fieldError(touched, errors, 'firstPayment')}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F3F4F6' } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
                {t('simulation.loanValue')}
              </Typography>
              <TextField
                fullWidth
                value={valueLoan}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#FFFFFF',
                    border: 1,
                    borderColor: '#2C2C2C',
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Taxas */}
          <Typography variant="subtitle2" sx={{ color: '#2C2C2C', fontWeight: 700, mb: 1 }}>
            {t('simulation.fees')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                name: 'interest',
                label: `${t('simulation.interest')} (% a.m.)`,
                type: 'percent',
                placeholder: '%',
              },
              { name: 'iof', label: 'IOF ou IMPOSTOS (%)', type: 'percent', placeholder: '%' },
              { name: 'tac', label: 'TAC (%)', type: 'percent', placeholder: '%' },
              {
                name: 'operational',
                label: `${t('simulation.operational')} (R$)`,
                type: 'monetary',
                placeholder: '0,00',
              },
            ].map(({ name, label, type, placeholder }) => (
              <Grid item xs={6} md={3} key={name}>
                <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
                  {label}
                </Typography>
                <TextField
                  fullWidth
                  name={name}
                  value={values[name]}
                  onChange={
                    type === 'percent' ? handlePercentChange(name) : handleMonetaryChange(name)
                  }
                  onBlur={handleBlur}
                  placeholder={placeholder}
                  error={touched[name] && !!errors[name]}
                  helperText={fieldError(touched, errors, name)}
                  disabled={simulation?.type === 'REDUTO_CAPITAL'}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F3F4F6' } }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Condicoes de Pagamento */}
          <Typography variant="subtitle2" sx={{ color: '#2C2C2C', fontWeight: 700, mb: 1 }}>
            {t('simulation.paymentTerms')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
            {t('simulation.paymentMethod')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { value: 'fixed', label: t('simulation.fixedInstallment') },
              { value: 'customized', label: t('simulation.customizedDate') },
            ].map(({ value, label }) => (
              <Grid item xs={12} md={6} key={value}>
                <Button
                  fullWidth
                  variant={values.paymentMethod === value ? 'contained' : 'outlined'}
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => {
                    setFieldValue('paymentMethod', value, true);
                  }}
                  sx={{
                    height: 48,
                    bgcolor:
                      values.paymentMethod === value ? colors.greenAccent[500] : 'transparent',
                    color: '#2C2C2C',
                    borderColor:
                      values.paymentMethod === value ? colors.greenAccent[500] : '#E5E7EB',
                    textTransform: 'none',
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                    '&:hover': {
                      bgcolor: values.paymentMethod === value ? '#73C43D' : '#F9FAFB',
                    },
                  }}
                >
                  {label}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Typography variant="body2" sx={{ color: '#2C2C2C', mb: 1, fontSize: '0.875rem' }}>
            {t('simulation.selectDatePayment')}
          </Typography>

          {isPaymentMethodFixed ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {FIXED_INSTALLMENT_OPTIONS.map((parcela) => (
                  <Grid item xs={6} md={2.5} key={parcela}>
                    <Button
                      fullWidth
                      variant={
                        values.parcelas === parcela && !isCustomInstallments
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => {
                        setIsCustomInstallments(false);
                        setFieldValue('parcelas', parcela);
                      }}
                      sx={{
                        height: 48,
                        bgcolor:
                          values.parcelas === parcela && !isCustomInstallments
                            ? '#FFFFFF'
                            : '#F3F4F6',
                        color: '#2C2C2C',
                        borderColor:
                          values.parcelas === parcela && !isCustomInstallments
                            ? '#2C2C2C'
                            : '#E5E7EB',
                        borderWidth: values.parcelas === parcela && !isCustomInstallments ? 2 : 1,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        '&:hover': { bgcolor: '#FFFFFF', borderColor: '#2C2C2C' },
                      }}
                    >
                      {parcela}x
                    </Button>
                  </Grid>
                ))}
                <Grid item xs={6} md={2}>
                  <Button
                    fullWidth
                    variant={isCustomInstallments ? 'contained' : 'outlined'}
                    onClick={() => {
                      setFieldValue('parcelas', 1);
                      setIsCustomInstallments(true);
                    }}
                    sx={{
                      height: 48,
                      bgcolor: isCustomInstallments ? colors.greenAccent[500] : '#FFFFFF',
                      color: '#2C2C2C',
                      borderColor: isCustomInstallments ? '#2C2C2C' : '#E5E7EB',
                      borderWidth: isCustomInstallments ? 2 : 1,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', md: '1rem' },
                      gap: { xs: '2px', md: '8px' },
                      px: { xs: 1, md: 2 },
                      '&:hover': { bgcolor: '#FFFFFF', borderColor: '#2C2C2C' },
                    }}
                  >
                    + {t('simulation.customize')}
                  </Button>
                </Grid>
              </Grid>

              {isCustomInstallments && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Button
                      variant="outlined"
                      onClick={decreaseValue}
                      sx={{
                        flex: 2,
                        minWidth: 0,
                        height: 48,
                        bgcolor: '#F3F4F6',
                        color: '#2C2C2C',
                        borderColor: '#E5E7EB',
                        padding: 0,
                        '&:hover': { bgcolor: '#FFFFFF', borderColor: '#2C2C2C' },
                      }}
                    >
                      <Remove />
                    </Button>
                    <Box
                      sx={{
                        flex: 3,
                        height: 48,
                        border: '1px solid #E5E7EB',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Input
                        type="number"
                        value={inputInstallment}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const val = parseInt(raw, 10);
                          if (raw === '' || isNaN(val)) {
                            setInputInstallment('');
                            return;
                          }
                          if (val > 60) {
                            setInputInstallment('60');
                            setFieldValue('parcelas', 60);
                            return;
                          }
                          if (val < 1) {
                            setInputInstallment('1');
                            setFieldValue('parcelas', 1);
                            return;
                          }
                          setInputInstallment(String(val));
                          setFieldValue('parcelas', val);
                        }}
                        onBlur={() => {
                          const val = parseInt(inputInstallment, 10);
                          if (isNaN(val) || val < 1) {
                            setInputInstallment('1');
                            setFieldValue('parcelas', 1);
                          } else if (val > 60) {
                            setInputInstallment('60');
                            setFieldValue('parcelas', 60);
                          }
                        }}
                        inputProps={{ max: 60, min: 1 }}
                        disableUnderline
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#2C2C2C',
                          width: '60px',
                          '& input': {
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#2C2C2C',
                            padding: 0,
                            MozAppearance: 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                            },
                          },
                        }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={incrementValue}
                      sx={{
                        flex: 2,
                        minWidth: 0,
                        height: 48,
                        bgcolor: '#F3F4F6',
                        color: '#2C2C2C',
                        borderColor: '#E5E7EB',
                        padding: 0,
                        '&:hover': { bgcolor: '#FFFFFF', borderColor: '#2C2C2C' },
                      }}
                    >
                      <Add />
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ mb: 3 }}>
              <CustomDateInstallments
                installments={customDateInstallments}
                onAdd={handleAddCustomDate}
                onRemove={handleRemoveCustomDate}
                onDateChange={handleCustomDateChange}
                onValueChange={handleCustomValueChange}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              fullWidth={false}
              variant="contained"
              startIcon={
                loadingSimulate ? (
                  <CircularProgress size={18} sx={{ color: '#2C2C2C' }} />
                ) : (
                  <CalculateIcon />
                )
              }
              disabled={loadingSimulate}
              sx={{
                height: 52,
                bgcolor: colors.greenAccent[500],
                color: '#2C2C2C',
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { bgcolor: '#73C43D' },
                '&:disabled': { bgcolor: '#D1D5DB', color: '#9CA3AF' },
              }}
            >
              {loadingSimulate ? t('simulation.simulating') : t('simulation.simulate')}
            </Button>
          </Box>
        </Paper>
      </form>

      <ModalRegisterClient
        open={createNewClient}
        onClose={() => setCreateNewClient(false)}
        onSubmit={handleCreateClient}
        loading={loadingClient}
      />
    </>
  );
};

export default SimulationForm;